/**
 * Daily Challenge admin page: types and pure helpers. Mirrors the backend
 * challenger module (wcc4-backend src/modules/challenger); the server checks
 * everything again on Validate and Publish.
 */

export type Metric = 'runs' | 'fours' | 'sixes' | 'boundaries' | 'consecutiveBoundaries';
export const METRICS: Metric[] = ['runs', 'fours', 'sixes', 'boundaries', 'consecutiveBoundaries'];

export type ObjectiveType =
  | 'SCORE_RUNS'
  | 'SCORE_RUNS_NO_WICKET'
  | 'HIT_SIXES'
  | 'HIT_FOURS'
  | 'HIT_BOUNDARIES'
  | 'CONSECUTIVE_BOUNDARIES'
  | 'MATCH_SITUATION'
  | 'CHASE_TARGET'
  | 'FINAL_OVER'
  | 'COMBO';

export const OBJECTIVE_TYPES: ObjectiveType[] = [
  'SCORE_RUNS',
  'SCORE_RUNS_NO_WICKET',
  'HIT_SIXES',
  'HIT_FOURS',
  'HIT_BOUNDARIES',
  'CONSECUTIVE_BOUNDARIES',
  'MATCH_SITUATION',
  'CHASE_TARGET',
  'FINAL_OVER',
  'COMBO',
];

export const OBJECTIVE_LABELS: Record<ObjectiveType, string> = {
  SCORE_RUNS: 'Score runs',
  SCORE_RUNS_NO_WICKET: 'Score runs without losing a wicket',
  HIT_SIXES: 'Hit sixes',
  HIT_FOURS: 'Hit fours',
  HIT_BOUNDARIES: 'Hit boundaries (4s + 6s)',
  CONSECUTIVE_BOUNDARIES: 'Consecutive boundaries',
  MATCH_SITUATION: 'Match situation (2nd innings)',
  CHASE_TARGET: 'Chase a target (2nd innings)',
  FINAL_OVER: 'Final over (2nd innings, 1 over)',
  COMBO: 'Combination',
};

export interface Condition {
  metric: Metric;
  min: number;
}

export interface Objective {
  type: ObjectiveType;
  conditions: Condition[];
  maxWicketsLost: number | null;
}

export interface Scenario {
  innings: 1 | 2;
  startingScore: number;
  wicketsLost: number;
  targetScore: number | null;
  overs: number;
  ballLimit: number;
  overNumber: number;
}

export interface PlayerPick {
  mode: 'any' | 'specific';
  playerId?: string;
}

export interface Gameplay {
  batter: PlayerPick;
  nonStriker: PlayerPick;
  bowler: {
    mode: 'any' | 'type' | 'specific';
    type?: 'fast' | 'medium' | 'spin';
    playerId?: string;
  };
  delivery?: { type?: string; line?: string; length?: string };
  field?: { preset?: string };
  stadiumId?: string;
  featuredPlayerId?: string | null;
  artKey?: string | null;
}

export interface ChallengeInput {
  key?: string;
  name: string;
  description?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  startAt: string;
  endAt: string;
  rolloutPct: number;
  objective: Objective;
  scenario: Scenario;
  gameplay: Gameplay;
  rewardOverride?: { coins: number; matchTokens: number } | null;
}

export interface Challenge extends ChallengeInput {
  id: string;
  key: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChallengeStats {
  playersStarted: number;
  completed: number;
  failed: number;
  successRate: number;
  attemptsByNo: Array<{ attemptNo: number; attempts: number; succeeded: number }>;
  adRetries: number;
  mismatches: number;
  flagged: number;
}

export interface ChallengerSettings {
  streakLength: number;
  dayRewards: Array<{ day: number; coins: number; matchTokens: number }>;
  milestones: Array<{
    day: number;
    coins?: number;
    cards?: { count: number; rarityWeights: Record<string, number> };
  }>;
  retry: { enabled: boolean; method: 'ad'; limit: number };
  freeAttempts: number;
  /** GDD §27 Streak Enabled; off = every success pays Day 1, no milestones. */
  streakEnabled?: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
}

export const CHASE_TYPES: ObjectiveType[] = ['MATCH_SITUATION', 'CHASE_TARGET', 'FINAL_OVER'];

const SINGLE: Partial<Record<ObjectiveType, [Metric, number]>> = {
  SCORE_RUNS: ['runs', 17],
  SCORE_RUNS_NO_WICKET: ['runs', 20],
  HIT_SIXES: ['sixes', 2],
  HIT_FOURS: ['fours', 3],
  HIT_BOUNDARIES: ['boundaries', 4],
  CONSECUTIVE_BOUNDARIES: ['consecutiveBoundaries', 3],
};

/** Starting objective for a type; chase types ask for exactly `runsRequired`. */
export function presetFor(type: ObjectiveType, runsRequired: number | null): Objective {
  if (CHASE_TYPES.includes(type)) {
    return {
      type,
      conditions: [{ metric: 'runs', min: runsRequired ?? 22 }],
      maxWicketsLost: null,
    };
  }
  if (type === 'COMBO') {
    return {
      type,
      conditions: [
        { metric: 'runs', min: 20 },
        { metric: 'sixes', min: 2 },
      ],
      maxWicketsLost: null,
    };
  }
  const [metric, min] = SINGLE[type]!;
  return {
    type,
    conditions: [{ metric, min }],
    maxWicketsLost: type === 'SCORE_RUNS_NO_WICKET' ? 0 : null,
  };
}

const IST_MS = 330 * 60_000;

/** IST calendar day (YYYY-MM-DD) → its UTC window, 18:30Z the day before to 18:30Z. */
export function istDayWindow(istDate: string): { startAt: string; endAt: string } {
  const start = Date.parse(`${istDate}T00:00:00.000Z`) - IST_MS;
  return {
    startAt: new Date(start).toISOString(),
    endAt: new Date(start + 86_400_000).toISOString(),
  };
}

/**
 * Window to save: the IST day, unless ops set a start or end time (short QA
 * windows, or a challenge ended early).
 */
export function challengeWindow(
  istDate: string,
  endAt: string | null,
  startAt: string | null = null
): { startAt: string; endAt: string } {
  const day = istDayWindow(istDate);
  return { startAt: startAt ?? day.startAt, endAt: endAt ?? day.endAt };
}

export function istDateOf(isoTime: string): string {
  return new Date(Date.parse(isoTime) + IST_MS).toISOString().slice(0, 10);
}

export function derive(s: Scenario): {
  runsRequired: number | null;
  wicketsRemaining: number;
  ballsRemaining: number;
} {
  return {
    runsRequired:
      s.innings === 2 && s.targetScore !== null ? s.targetScore - s.startingScore : null,
    wicketsRemaining: 10 - s.wicketsLost,
    ballsRemaining: s.ballLimit,
  };
}

/** One-line objective text, as the player sees it. */
export function objectiveText(o: Objective, s: Scenario): string {
  const names: Record<Metric, string> = {
    runs: 'runs',
    fours: 'fours',
    sixes: 'sixes',
    boundaries: 'boundaries',
    consecutiveBoundaries: 'consecutive boundaries',
  };
  const parts = o.conditions.map((c) => `${c.min} ${names[c.metric]}`);
  const tail = o.maxWicketsLost === 0 ? ' without losing a wicket' : '';
  return `${parts.join(' + ')} in ${s.ballLimit} balls${tail}`;
}

export const DEFAULT_SCENARIO: Scenario = {
  innings: 2,
  startingScore: 230,
  wicketsLost: 9,
  targetScore: 252,
  overs: 1,
  ballLimit: 6,
  overNumber: 20,
};
